import {
  useMemo,
  useContext,
  useState,
  useRef,
  useLayoutEffect,
  type FC,
  type MouseEvent,
  type ReactNode,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { TFunction, useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import { colorsButton } from 'app/colors';
import IconButton from '__components/Kizen/IconButton';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import { useTooltip, truncationPredicates } from '__components/Kizen/Tooltip';
import Handle from '../Handle';
import { Button as ButtonKDS } from '@kizen/kds/Button';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import {
  StyledMenuSelect,
  ThreeDotsMenu,
  RerunContainer,
  StyledMenu,
  WrapperCSSProps,
  StyledCardSubheader,
  StyledCardHeader,
  StyledCard,
  StyledBody,
  StyledClickableLink,
  type StepLabel,
  StyledPluginApp,
} from './styles';
import { Searchable, SearchableStepId } from './Searchable';
import { Position } from 'reactflow';
import type { GenericOption } from 'pages/AutomationEngine/types';
import { Badge, BadgeProps } from '@kizen/kds/Badge';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { AUTOMATION_STEPS, StepNodeType } from 'pages/AutomationEngine/steps';
import { type MarketplacePlugin } from 'pages/Marketplace/types';
import { getFilePath } from 'pages/Marketplace/components/Tile';
import { Typography } from '@kizen/kds/Typography';
import { EditableDescription } from './EditableDescription';

const overflowXOrY = (el: HTMLElement) =>
  truncationPredicates.x(el) || truncationPredicates.y(el);

const MENU_OPTION_HEIGHT = 32;
const MIN_VISIBLE_OPTIONS = 5;
const ADD_DESCRIPTION_KEY = 'add_user_description';

const CardMenu: FC<{
  menuOptions: GenericOption[];
  onSelectMenu?: null | ((option: GenericOption) => void);
  editOnly?: boolean;
  disableMenu?: boolean;
}> = ({ menuOptions, onSelectMenu, editOnly, disableMenu }) => {
  const { t } = useTranslation();

  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<Element | null>(null);

  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: string | number;
    bottom: string | number;
    left: string | number;
    right: string | number;
  } | null>(null);

  const requiredMenuHeight =
    Math.min(menuOptions.length, MIN_VISIBLE_OPTIONS) * MENU_OPTION_HEIGHT;

  const onButtonClick = () => {
    if (editOnly) {
      onSelectMenu?.({ value: 'edit', label: t('Edit') });
    } else {
      setShowMenu(true);
    }
  };

  const lastRectRef = useRef<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (showMenu) {
      let frameId: number | null = null;
      const button = buttonRef.current;
      const container = document.querySelector('.FlowContainer');
      containerRef.current = container;

      const syncPosition = () => {
        if (button && container) {
          const lastRect = lastRectRef.current;
          const rect = button.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (
            !lastRect ||
            lastRect.top !== rect.top ||
            lastRect.right !== rect.right
          ) {
            lastRectRef.current = rect;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < requiredMenuHeight;
            setMenuPosition({
              top: openAbove ? 'auto' : rect.bottom - containerRect.top - 2,
              bottom: openAbove ? window.innerHeight - rect.top : 'auto',
              right: window.innerWidth - rect.left - rect.width,
              left: 'auto',
            });
          }
        }
        frameId = requestAnimationFrame(syncPosition);
      };
      syncPosition();

      const handleCloseMenu = (e: Event) => {
        const target = e.target as Node | null;
        if (
          !buttonRef.current?.contains(target) &&
          !menuRef.current?.contains(target)
        ) {
          setShowMenu(false);
        }
      };
      document.addEventListener('click', handleCloseMenu, {
        capture: true,
      });
      return () => {
        document.removeEventListener('click', handleCloseMenu, {
          capture: true,
        });
        if (frameId) {
          cancelAnimationFrame(frameId);
        }
        lastRectRef.current = null;
      };
    }
  }, [showMenu, requiredMenuHeight]);

  return (
    <ThreeDotsMenu ref={buttonRef}>
      <IconButton
        title={t('Edit Item')}
        disabled={disableMenu}
        onClick={onButtonClick}
        color={
          showMenu
            ? 'var(--color-button-tertiary-hover)'
            : colorsButton.iconGray
        }
        sizing="dense"
      >
        <IconSizing size={editOnly ? '12px' : '15px'}>
          <Icon icon={editOnly ? 'pen' : 'three-dot'} />
        </IconSizing>
      </IconButton>
      {editOnly || !showMenu || !menuPosition || !lastRectRef.current
        ? null
        : createPortal(
            <StyledMenu ref={menuRef} style={menuPosition}>
              <StyledMenuSelect
                menuOnly
                menuIsOpen
                onChange={(val: GenericOption) => {
                  setShowMenu(false);
                  onSelectMenu?.(val);
                }}
                options={menuOptions}
                data-qa="card-action-menu"
              />
            </StyledMenu>,
            containerRef.current!
          )}
    </ThreeDotsMenu>
  );
};

const getLabelTypeText = (stepLabelType: StepLabel, t: TFunction) => {
  switch (stepLabelType) {
    case 'skipped':
      return t('SKIPPED');
    case 'unreachable':
      return t('UNREACHABLE');
    case 'created':
      return t('CREATED');
    case 'updated':
      return t('UPDATED');
    default:
      return '';
  }
};

const PluginApp: FC<{ pluginApp?: MarketplacePlugin }> = ({ pluginApp }) => {
  if (!pluginApp?.name) {
    return null;
  }
  const { name, thumbnail } = pluginApp;
  return (
    <StyledPluginApp>
      {thumbnail && <img src={getFilePath(thumbnail)} alt={name} />}
      <Typography size="md" truncate>
        {name}
      </Typography>
    </StyledPluginApp>
  );
};

export type StepCardProps = {
  type?: StepNodeType;
  badgeProps?: BadgeProps;
  subtypeLabel?: string | null;
  panel?: ReactNode | null;
  userDescription?: string | null;
  addMenu?: boolean;
  onSelectMenu?: null | ((option: GenericOption) => void);
  disableMenu?: boolean;
  children?: ReactNode;
  menuOptions?: { label: string; value: string }[];
  showMovingPlaceholder?: boolean;
  showStepId?: boolean;
  editOnly?: boolean;
  showRestart?: boolean;
  leading?: ReactNode | null;
  id: string;
  isSmartConnectorFlow?: boolean;
  pluginApp?: MarketplacePlugin;
  onEditUserDescription?: (userDescription: string) => void;
} & WrapperCSSProps;

export const StepCard: FC<StepCardProps> = ({
  type,
  badgeProps,
  subtypeLabel,
  panel = null,
  userDescription = null,
  addMenu = true,
  onSelectMenu: onSelectMenuProp = null,
  disableMenu = false,
  children,
  menuOptions: menuOptionsProp = EMPTY_ARRAY,
  showMovingPlaceholder,
  showStepId,
  editOnly = false,
  showRestart,
  leading = null,
  id,
  dragging = false,
  stepLabelType,
  pluginApp,
  onEditUserDescription,
  ...props
}) => {
  const { t } = useTranslation();
  const { setRerunModalStepId } = useContext(ExecutionHistoryContext);
  const [hovered, setHovered] = useState(false);
  const [isUserDescriptionOpen, setUserDescriptionOpen] = useState(false);

  const { label, color, bgColor, icon } = badgeProps ?? {
    ...AUTOMATION_STEPS.unknown,
    label: AUTOMATION_STEPS.unknown.label(t),
  };

  const [tooltipProps, tooltip] = useTooltip({ html: true }, overflowXOrY);

  const onSelectMenu = useCallback(
    (option: GenericOption) => {
      setHovered(false);
      if (option.value === ADD_DESCRIPTION_KEY) {
        setUserDescriptionOpen(true);
      } else {
        onSelectMenuProp?.(option);
      }
    },
    [onSelectMenuProp]
  );

  const onChangeUserDescription = useCallback(
    (value: string) => {
      onEditUserDescription?.(value);
      setHovered(false);
    },
    [onEditUserDescription]
  );

  const onSelectEdit = (e: MouseEvent<HTMLDivElement>) => {
    e?.preventDefault();
    onSelectMenu?.({ value: 'edit', label: t('Edit') });
  };

  const onMouseEnter = () => {
    setHovered(true);
  };

  const onMouseLeave = () => {
    setHovered(false);
  };

  const hasUserDescription = !!userDescription;

  const menuOptions = useMemo(() => {
    if (!hasUserDescription) {
      return [
        ...menuOptionsProp,
        {
          label: t('Add Description'),
          value: ADD_DESCRIPTION_KEY,
        },
      ];
    }
    return menuOptionsProp;
  }, [hasUserDescription, menuOptionsProp, t]);

  const hasEditOption = useMemo(() => {
    return addMenu && menuOptions.some((option) => option.value === 'edit');
  }, [addMenu, menuOptions]);

  const enableEditLink = hasEditOption && !disableMenu;

  return (
    <StyledCard
      {...props}
      dragging={dragging}
      stepLabelType={stepLabelType}
      stepLabelText={stepLabelType && getLabelTypeText(stepLabelType, t)}
      onMouseEnter={onMouseEnter}
      onMouseOver={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {leading}
      <StyledCardHeader>
        <Searchable stepId={id} enabled>
          <Badge color={color} bgColor={bgColor} label={label} icon={icon} />
        </Searchable>
        {addMenu ? (
          <CardMenu
            menuOptions={menuOptions}
            onSelectMenu={onSelectMenu}
            disableMenu={disableMenu}
            editOnly={editOnly}
          />
        ) : showRestart || showStepId ? (
          <RerunContainer>
            {showStepId ? <SearchableStepId stepId={id} enabled /> : null}
            {showRestart ? (
              <ButtonKDS
                onClick={() => setRerunModalStepId(id)}
                leftIcon="action-automation-debug-rerun-step"
                variant="text"
                color="secondary"
                size="small"
              />
            ) : null}
          </RerunContainer>
        ) : null}
      </StyledCardHeader>
      {subtypeLabel ? (
        <StyledCardSubheader
          {...tooltipProps}
          onClick={enableEditLink ? onSelectEdit : undefined}
        >
          <Searchable stepId={id} enabled>
            {enableEditLink ? (
              <StyledClickableLink weight="bold" type="anchor" href="">
                {subtypeLabel}
              </StyledClickableLink>
            ) : (
              <TextEllipsisWithTooltip weight="bold">
                {subtypeLabel}
              </TextEllipsisWithTooltip>
            )}
          </Searchable>
        </StyledCardSubheader>
      ) : null}
      {children && (
        <>
          <StyledBody {...tooltipProps} data-qa="card-body">
            <Searchable stepId={id} enabled>
              {typeof children !== 'string' && children}
              {typeof children === 'string' && (
                <KizenTypography as="span">{children}</KizenTypography>
              )}
            </Searchable>
          </StyledBody>
          {tooltip}
        </>
      )}
      {onEditUserDescription ? (
        <EditableDescription
          isOpen={isUserDescriptionOpen}
          setIsOpen={setUserDescriptionOpen}
          userDescription={userDescription}
          stepId={id}
          hovered={hovered}
          editable={!disableMenu && addMenu}
          onChange={onChangeUserDescription}
        />
      ) : null}
      {pluginApp ? <PluginApp pluginApp={pluginApp} /> : null}
      {panel}
      <Handle
        id="handle-left"
        type="target"
        position={Position.Left}
        style={{ left: 0 }}
      />
      <Handle
        id="handle-right"
        type="source"
        position={Position.Right}
        style={{ right: 0 }}
      />
      <Handle
        id="handle-top"
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
      />
      <Handle
        id="handle-bottom"
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
      />
      <Handle
        id="handle-bottom-target"
        type="target"
        position={Position.Bottom}
        style={{ bottom: 0 }}
      />
    </StyledCard>
  );
};
